import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAccount, updateAccount, updateEmailOrganizationName, formatWeeksOfMonth, resolveWeeksOfMonth, type EmailBranding, type RecurrencePreset, type RecurrencePattern, type WeekOfMonth } from '@littlebooker/dal';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { testWebhook } from '@/lib/webhook';
import { Building2, Save, Clock, Phone, Mail, Palette, Plus, X, Globe, Loader2 } from 'lucide-react';
import { getCountries, getCountryCallingCode, type CountryCode } from 'react-phone-number-input';

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Paris', label: 'Europe/Paris' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin' },
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
];

// Get all countries for the dropdown
const getCountryOptions = () => {
  const europeanCountries: CountryCode[] = ['NL', 'BE', 'DE', 'FR', 'GB', 'IT', 'ES', 'PT', 'AT', 'CH', 'SE', 'NO', 'DK', 'FI', 'PL'];
  const allCountries = getCountries();

  // Sort: European countries first, then others alphabetically
  const sorted = [...allCountries].sort((a, b) => {
    const aIsEuro = europeanCountries.includes(a);
    const bIsEuro = europeanCountries.includes(b);
    if (aIsEuro && !bIsEuro) return -1;
    if (!aIsEuro && bIsEuro) return 1;
    const aName = new Intl.DisplayNames(['en'], { type: 'region' }).of(a) || a;
    const bName = new Intl.DisplayNames(['en'], { type: 'region' }).of(b) || b;
    return aName.localeCompare(bName);
  });

  return sorted.map(code => ({
    code,
    name: new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code,
    callingCode: getCountryCallingCode(code),
  }));
};

export default function AccountSettingsPage() {
  const { account, user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(account?.name || '');
  const [timezone, setTimezone] = useState('UTC');
  const [defaultCountryCode, setDefaultCountryCode] = useState<CountryCode>('NL');
  const [preferredImageRatio, setPreferredImageRatio] = useState<'landscape' | 'portrait'>('landscape');

  // Email branding state
  const [emailBranding, setEmailBranding] = useState<EmailBranding>({});

  // Recurrence presets state
  const [recurrencePresets, setRecurrencePresets] = useState<RecurrencePreset[]>([]);

  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookTestStatus, setWebhookTestStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);

  // Post-show email state
  const [postShowFeedbackUrl, setPostShowFeedbackUrl] = useState('');
  const [postShowDefaultOffsetHours, setPostShowDefaultOffsetHours] = useState(24);

  // Public website state
  const [websiteBaseUrl, setWebsiteBaseUrl] = useState('');

  // Artist invite defaults state
  const [defaultGenerateArtistLink, setDefaultGenerateArtistLink] = useState(true);
  const [defaultInviteEmailStrategy, setDefaultInviteEmailStrategy] = useState<'combined' | 'separate' | 'none'>('combined');

  const countryOptions = getCountryOptions();

  const isOwner = user?.role === 'owner';

  const { data: accountData, isLoading } = useQuery({
    queryKey: ['account', account?.id],
    queryFn: () => (account?.id ? getAccount(account.id) : null),
    enabled: !!account?.id,
  });

  // Update local state when account data loads
  useEffect(() => {
    if (accountData?.name) {
      setName(accountData.name);
    }
    if (accountData?.timezone) {
      setTimezone(accountData.timezone);
    }
    if (accountData?.settings && typeof accountData.settings === 'object') {
      const settings = accountData.settings as Record<string, unknown>;
      if (settings.default_country_code && typeof settings.default_country_code === 'string') {
        setDefaultCountryCode(settings.default_country_code as CountryCode);
      }
      if (settings.preferred_image_ratio === 'portrait') {
        setPreferredImageRatio('portrait');
      } else {
        setPreferredImageRatio('landscape');
      }
      // Load email branding
      if (settings.emailBranding && typeof settings.emailBranding === 'object') {
        setEmailBranding(settings.emailBranding as EmailBranding);
      } else {
        setEmailBranding({});
      }
      // Load recurrence presets
      if (settings.recurrence_presets && Array.isArray(settings.recurrence_presets)) {
        setRecurrencePresets(settings.recurrence_presets as RecurrencePreset[]);
      } else {
        setRecurrencePresets([]);
      }
      // Load webhook settings
      if (settings.webhook && typeof settings.webhook === 'object') {
        const webhook = settings.webhook as { url?: string; secret?: string };
        setWebhookUrl(webhook.url || '');
        setWebhookSecret(webhook.secret || '');
      } else {
        setWebhookUrl('');
        setWebhookSecret('');
      }
      // Load post-show email settings
      if (typeof settings.postShowFeedbackUrl === 'string') {
        setPostShowFeedbackUrl(settings.postShowFeedbackUrl);
      }
      if (typeof settings.postShowDefaultOffsetHours === 'number') {
        setPostShowDefaultOffsetHours(settings.postShowDefaultOffsetHours);
      }
      if (typeof settings.websiteBaseUrl === 'string') {
        setWebsiteBaseUrl(settings.websiteBaseUrl);
      } else {
        setWebsiteBaseUrl('');
      }
      // Load artist invite defaults
      if (typeof settings.defaultGenerateArtistLink === 'boolean') {
        setDefaultGenerateArtistLink(settings.defaultGenerateArtistLink);
      }
      if (settings.defaultInviteEmailStrategy === 'combined' || settings.defaultInviteEmailStrategy === 'separate' || settings.defaultInviteEmailStrategy === 'none') {
        setDefaultInviteEmailStrategy(settings.defaultInviteEmailStrategy);
      }
    }
  }, [accountData?.name, accountData?.timezone, accountData?.settings]);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; timezone?: string; settings?: Record<string, unknown> }) => {
      if (!account?.id) throw new Error('No account ID');
      return updateAccount(account.id, data);
    },
    onSuccess: (updatedAccount) => {
      queryClient.invalidateQueries({ queryKey: ['account', account?.id] });
      // Update local state
      if (updatedAccount.name) {
        setName(updatedAccount.name);
      }
      if (updatedAccount.timezone) {
        setTimezone(updatedAccount.timezone);
      }
      // Update email service with new organization name
      if (updatedAccount.name) {
        updateEmailOrganizationName(updatedAccount.name);
      }
      // Invalidate happenings and tasks queries so they re-render with new timezone
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      // Update local state for settings
      if (updatedAccount.settings && typeof updatedAccount.settings === 'object') {
        const settings = updatedAccount.settings as Record<string, unknown>;
        if (settings.default_country_code && typeof settings.default_country_code === 'string') {
          setDefaultCountryCode(settings.default_country_code as CountryCode);
        }
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;
    const updates: { name?: string; timezone?: string; settings?: Record<string, unknown> } = {};
    if (name.trim() !== (accountData?.name || '')) {
      updates.name = name.trim();
    }
    if (timezone !== (accountData?.timezone || 'UTC')) {
      updates.timezone = timezone;
    }

    // Update settings with default country code and preferred image ratio
    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};
    const currentDefaultCountry = (currentSettings.default_country_code as CountryCode) || 'NL';
    const currentImageRatio = (currentSettings.preferred_image_ratio as string) || 'landscape';

    if (defaultCountryCode !== currentDefaultCountry || preferredImageRatio !== currentImageRatio) {
      updates.settings = {
        ...currentSettings,
        default_country_code: defaultCountryCode,
        preferred_image_ratio: preferredImageRatio,
      };
    }

    if (Object.keys(updates).length > 0) {
      updateMutation.mutate(updates);
    }
  };

  const handleEmailBrandingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    // Clean up empty branding fields
    const cleanedBranding: EmailBranding = {};
    if (emailBranding.replyToEmail?.trim()) cleanedBranding.replyToEmail = emailBranding.replyToEmail.trim();
    if (emailBranding.logoUrl?.trim()) cleanedBranding.logoUrl = emailBranding.logoUrl.trim();
    if (emailBranding.primaryColor?.trim()) cleanedBranding.primaryColor = emailBranding.primaryColor.trim();
    if (emailBranding.secondaryColor?.trim()) cleanedBranding.secondaryColor = emailBranding.secondaryColor.trim();
    if (emailBranding.footerText?.trim()) cleanedBranding.footerText = emailBranding.footerText.trim();
    if (emailBranding.templates) cleanedBranding.templates = emailBranding.templates;

    // Build new settings object
    const newSettings = { ...currentSettings };

    if (Object.keys(cleanedBranding).length > 0) {
      // Update emailBranding with cleaned values
      newSettings.emailBranding = cleanedBranding;
    } else {
      // Remove emailBranding if it's empty
      delete newSettings.emailBranding;
    }

    const updates = {
      settings: newSettings,
    };

    updateMutation.mutate(updates);
  };

  const handleRecurrencePresetsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    const updates = {
      settings: {
        ...currentSettings,
        recurrence_presets: recurrencePresets,
      },
    };

    updateMutation.mutate(updates);
  };

  const handleWebhookSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    const newSettings = { ...currentSettings };

    const cleanedWebhook: { url?: string; secret?: string } = {};
    if (webhookUrl.trim()) cleanedWebhook.url = webhookUrl.trim();
    if (webhookSecret.trim()) cleanedWebhook.secret = webhookSecret.trim();

    if (Object.keys(cleanedWebhook).length > 0) {
      newSettings.webhook = cleanedWebhook;
    } else {
      delete newSettings.webhook;
    }

    // Webhook firing is server-side now (LittleBooker API reads
    // accounts.settings.webhook on every fire), so no in-memory config
    // to update after save — the next notify call will pick up the new
    // URL / secret automatically.
    updateMutation.mutate({ settings: newSettings });
  };

  const handleTestWebhook = async () => {
    const url = webhookUrl.trim();
    if (!url) {
      setWebhookTestStatus({ type: 'error', message: 'Enter a webhook URL first' });
      return;
    }

    setIsTestingWebhook(true);
    setWebhookTestStatus(null);

    // testWebhook now hits the backend API which reads the SAVED webhook
    // URL/secret from the account row, so it pings whatever's currently
    // persisted — not the unsaved form values. Save before testing if you
    // changed the URL.
    const result = await testWebhook();

    if (result.ok) {
      setWebhookTestStatus({ type: 'success', message: `Webhook responded with ${result.status}` });
    } else if (result.status) {
      setWebhookTestStatus({ type: 'error', message: `Webhook returned HTTP ${result.status}` });
    } else {
      setWebhookTestStatus({ type: 'error', message: result.error || 'Connection failed' });
    }

    setIsTestingWebhook(false);
  };

  const handlePostShowSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    const newSettings = { ...currentSettings };

    if (postShowFeedbackUrl.trim()) {
      newSettings.postShowFeedbackUrl = postShowFeedbackUrl.trim();
    } else {
      delete newSettings.postShowFeedbackUrl;
    }

    newSettings.postShowDefaultOffsetHours = postShowDefaultOffsetHours;

    updateMutation.mutate({ settings: newSettings });
  };

  const handleWebsiteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    const newSettings = { ...currentSettings };
    const trimmed = websiteBaseUrl.trim().replace(/\/+$/, '');
    if (trimmed) {
      newSettings.websiteBaseUrl = trimmed;
    } else {
      delete newSettings.websiteBaseUrl;
    }

    updateMutation.mutate({ settings: newSettings });
  };

  const handleArtistInviteDefaultsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) return;

    const currentSettings = (accountData?.settings && typeof accountData.settings === 'object'
      ? accountData.settings as Record<string, unknown>
      : {}) || {};

    const newSettings = {
      ...currentSettings,
      defaultGenerateArtistLink: defaultGenerateArtistLink,
      defaultInviteEmailStrategy: defaultInviteEmailStrategy,
    };

    updateMutation.mutate({ settings: newSettings });
  };

  if (!account) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Account Settings</h1>
          <p className="text-muted-foreground">No account information available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Account Settings</p>
        <h1 className="text-3xl font-bold">Organization</h1>
        <p className="text-muted-foreground">
          {isOwner
            ? 'Manage your organization settings. Changes to the organization name will appear in email subjects. Timezone changes affect how dates and times are displayed throughout the application.'
            : 'Only owners can edit organization settings.'}
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organization Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isOwner || updateMutation.isPending}
              className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
              placeholder="Your Organization Name"
            />
            <p className="text-xs text-muted-foreground">
              This name will appear at the beginning of all email subjects (e.g., "{name || 'Your Organization'} - Task Reminder: ...")
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!isOwner || updateMutation.isPending}
              className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
            >
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              All dates and times will be displayed and edited in this timezone. The backend always stores times in UTC.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Default Country Code
            </label>
            <select
              value={defaultCountryCode}
              onChange={(e) => setDefaultCountryCode(e.target.value as CountryCode)}
              disabled={!isOwner || updateMutation.isPending}
              className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
            >
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name} ({country.callingCode})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              This country code will be pre-selected by default in all phone number fields throughout the application.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              Preferred Image Ratio
            </label>
            <select
              value={preferredImageRatio}
              onChange={(e) => setPreferredImageRatio(e.target.value as 'landscape' | 'portrait')}
              disabled={!isOwner || updateMutation.isPending}
              className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
            >
              <option value="landscape">Landscape (3:2)</option>
              <option value="portrait">Portrait (3:4)</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Controls the expected image orientation throughout the app. Upload warnings and image previews will adapt to this setting.
            </p>
          </div>

          {isOwner && (
            <Button
              type="submit"
              disabled={
                updateMutation.isPending ||
                (name.trim() === (accountData?.name || '') &&
                  timezone === (accountData?.timezone || 'UTC') &&
                  defaultCountryCode === ((accountData?.settings && typeof accountData.settings === 'object'
                    ? (accountData.settings as Record<string, unknown>).default_country_code as CountryCode
                    : 'NL') || 'NL') &&
                  preferredImageRatio === ((accountData?.settings && typeof accountData.settings === 'object'
                    ? (accountData.settings as Record<string, unknown>).preferred_image_ratio as string
                    : 'landscape') || 'landscape'))
              }
            >
              <Save className="mr-2 h-4 w-4" />
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          )}

          {updateMutation.isError && (
            <div className="text-sm text-destructive">
              Failed to save changes: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
            </div>
          )}

          {updateMutation.isSuccess && (
            <div className="text-sm text-green-600">
              Organization settings updated successfully!
            </div>
          )}
        </form>
      )}

      {/* Email Branding Section */}
      <div className="mt-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Email Branding</p>
          <h2 className="text-2xl font-bold">Customize Email Templates</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Customize the appearance and copy of automated emails sent by LittleBooker. Add your logo, brand colors, and custom messaging.'
              : 'Only owners can edit email branding settings.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handleEmailBrandingSubmit} className="space-y-6 max-w-2xl mt-6">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Reply-To Email Address
              </label>
              <input
                type="email"
                value={emailBranding.replyToEmail || ''}
                onChange={(e) => setEmailBranding({ ...emailBranding, replyToEmail: e.target.value })}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                placeholder="e.g., booking@yourvenue.com"
              />
              <p className="text-xs text-muted-foreground">
                Artists can reply to automated emails at this address. Leave empty to disable replies.
              </p>
            </div>

            <PhotoUploadField
              label="Organization Logo"
              helper="Upload your organization logo or provide a URL. Recommended size: 200x60px or similar aspect ratio. The logo will appear at the top of all email templates."
              value={emailBranding.logoUrl || ''}
              onChange={(url) => setEmailBranding({ ...emailBranding, logoUrl: url })}
              category="account_logo"
              identifier={account?.slug || 'logo'}
              accountSlug={account?.slug}
              placeholder="https://example.com/logo.png"
              allowBypassSizeLimit
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={emailBranding.primaryColor || '#667eea'}
                    onChange={(e) => setEmailBranding({ ...emailBranding, primaryColor: e.target.value })}
                    disabled={!isOwner || updateMutation.isPending}
                    className="h-10 w-20 rounded border disabled:opacity-50"
                  />
                  <input
                    type="text"
                    value={emailBranding.primaryColor || ''}
                    onChange={(e) => setEmailBranding({ ...emailBranding, primaryColor: e.target.value })}
                    disabled={!isOwner || updateMutation.isPending}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                    placeholder="#667eea"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Used for headers and buttons
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={emailBranding.secondaryColor || '#764ba2'}
                    onChange={(e) => setEmailBranding({ ...emailBranding, secondaryColor: e.target.value })}
                    disabled={!isOwner || updateMutation.isPending}
                    className="h-10 w-20 rounded border disabled:opacity-50"
                  />
                  <input
                    type="text"
                    value={emailBranding.secondaryColor || ''}
                    onChange={(e) => setEmailBranding({ ...emailBranding, secondaryColor: e.target.value })}
                    disabled={!isOwner || updateMutation.isPending}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                    placeholder="#764ba2"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Used for gradient backgrounds
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Footer Text
              </label>
              <textarea
                value={emailBranding.footerText || ''}
                onChange={(e) => setEmailBranding({ ...emailBranding, footerText: e.target.value })}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50 min-h-[80px]"
                placeholder="This is an automated reminder from LittleBooker."
              />
              <p className="text-xs text-muted-foreground">
                Custom footer text that appears at the bottom of all emails. Leave empty to use default.
              </p>
            </div>

            {isOwner && (
              <Button
                type="submit"
                disabled={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Email Branding'}
              </Button>
            )}

            {updateMutation.isError && (
              <div className="text-sm text-destructive">
                Failed to save email branding: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="text-sm text-green-600">
                Email branding updated successfully!
              </div>
            )}
          </form>
        )}
      </div>

      {/* Website Integration Section */}
      <div className="mt-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Website Integration</p>
          <h2 className="text-2xl font-bold">Webhook Configuration</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Configure a webhook URL to automatically notify your website when shows or artists are updated. This triggers page revalidation for instant content updates.'
              : 'Only owners can edit webhook settings.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handleWebhookSubmit} className="space-y-6 max-w-2xl mt-6">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Webhook URL
              </label>
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                placeholder="https://yourwebsite.com/api/webhook"
              />
              <p className="text-xs text-muted-foreground">
                The endpoint that will receive POST requests when content changes. Leave empty to disable webhooks.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Webhook Secret
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                placeholder="optional shared secret"
              />
              <p className="text-xs text-muted-foreground">
                Optional secret sent as a Bearer token in the Authorization header for request verification.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {isOwner && (
                <>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Webhook Settings'}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    disabled={isTestingWebhook || !webhookUrl.trim()}
                    onClick={handleTestWebhook}
                  >
                    {isTestingWebhook ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Globe className="mr-2 h-4 w-4" />
                    )}
                    {isTestingWebhook ? 'Testing...' : 'Test Webhook'}
                  </Button>
                </>
              )}
            </div>

            {webhookTestStatus && (
              <div className={`text-sm ${webhookTestStatus.type === 'success' ? 'text-green-600' : 'text-destructive'}`}>
                {webhookTestStatus.message}
              </div>
            )}

            {updateMutation.isError && (
              <div className="text-sm text-destructive">
                Failed to save webhook settings: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="text-sm text-green-600">
                Webhook settings updated successfully!
              </div>
            )}
          </form>
        )}
      </div>

      {/* Recurrence Presets Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold">Series Recurrence Presets</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Configure recurrence patterns that will be available when creating series. This is useful for radios that only stream on specific days.'
              : 'Only owners can edit recurrence presets.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handleRecurrencePresetsSubmit} className="space-y-6 max-w-2xl">
            <div className="space-y-3">
              {recurrencePresets.map((preset, index) => (
                <div key={index} className="flex items-center gap-2 rounded-md border bg-muted/30 p-3">
                  <div className="flex-1">
                    <p className="font-medium">{preset.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {preset.pattern.frequency === 'weekly'
                        ? `Every ${preset.pattern.dayOfWeek}`
                        : `${formatWeeksOfMonth(resolveWeeksOfMonth(preset.pattern))} ${preset.pattern.dayOfWeek}${preset.pattern.frequency === 'bi-monthly' ? ' every 2 months' : ' of the month'}`}
                    </p>
                  </div>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setRecurrencePresets(recurrencePresets.filter((_, i) => i !== index));
                      }}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}

              {recurrencePresets.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                  No recurrence presets configured yet.
                  {isOwner && ' Click "Add Preset" below to add one.'}
                </p>
              )}
            </div>

            {isOwner && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const name = prompt('Enter a name for this preset (e.g., "Every Monday", "First Wednesday"):');
                    if (!name?.trim()) return;

                    const frequency = prompt('Enter frequency (weekly, monthly, or bi-monthly):');
                    if (frequency !== 'weekly' && frequency !== 'monthly' && frequency !== 'bi-monthly') {
                      alert('Frequency must be "weekly", "monthly", or "bi-monthly"');
                      return;
                    }

                    const dayOfWeek = prompt('Enter day (MO, TU, WE, TH, FR, SA, SU):')?.toUpperCase();
                    if (!['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].includes(dayOfWeek || '')) {
                      alert('Invalid day of week');
                      return;
                    }

                    let pattern: RecurrencePattern;
                    if (frequency === 'weekly') {
                      pattern = { frequency: 'weekly', dayOfWeek: dayOfWeek as any };
                    } else {
                      const weeksInput = prompt(
                        'Enter weeks of month, comma-separated (1-4 for first through fourth, -1 for last).\nE.g. "2,4,-1" for the 2nd, 4th & last:'
                      );
                      const weeks = (weeksInput || '')
                        .split(',')
                        .map((part) => parseInt(part.trim(), 10));
                      if (weeks.length === 0 || weeks.some((w) => ![1, 2, 3, 4, -1].includes(w))) {
                        alert('Each week of month must be 1-4 or -1');
                        return;
                      }
                      const uniqueWeeks = [1, 2, 3, 4, -1].filter((w) => weeks.includes(w)) as WeekOfMonth[];
                      pattern = {
                        frequency: frequency === 'bi-monthly' ? 'bi-monthly' : 'monthly',
                        weeksOfMonth: uniqueWeeks,
                        dayOfWeek: dayOfWeek as any,
                      };
                    }

                    setRecurrencePresets([...recurrencePresets, { name: name.trim(), pattern }]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Preset
                </Button>

                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {updateMutation.isPending ? 'Saving...' : 'Save Recurrence Presets'}
                </Button>
              </>
            )}

            {updateMutation.isError && (
              <div className="text-sm text-destructive">
                Failed to save: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="text-sm text-green-600">
                Recurrence presets updated successfully!
              </div>
            )}
          </form>
        )}
      </div>

      {/* Post-Show Email Section */}
      <div className="mt-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Post-Show Email</p>
          <h2 className="text-2xl font-bold">Artist Follow-up</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Configure the default settings for post-show follow-up emails sent to artists after their shows.'
              : 'Only owners can edit post-show email settings.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handlePostShowSubmit} className="space-y-6 max-w-2xl mt-6">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Default Feedback Form URL
              </label>
              <input
                type="url"
                value={postShowFeedbackUrl}
                onChange={(e) => setPostShowFeedbackUrl(e.target.value)}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                placeholder="https://forms.example.com/feedback"
              />
              <p className="text-xs text-muted-foreground">
                Link to an external feedback form included in post-show emails. Can be overridden per show. Leave empty to omit.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Default Send Delay
              </label>
              <select
                value={postShowDefaultOffsetHours}
                onChange={(e) => setPostShowDefaultOffsetHours(parseInt(e.target.value, 10))}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
              >
                <option value={1}>1 hour after show</option>
                <option value={6}>6 hours after show</option>
                <option value={12}>12 hours after show</option>
                <option value={24}>1 day after show</option>
                <option value={48}>2 days after show</option>
                <option value={72}>3 days after show</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Default delay before sending the post-show email. Can be overridden per show.
              </p>
            </div>

            {isOwner && (
              <Button
                type="submit"
                disabled={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Post-Show Settings'}
              </Button>
            )}

            {updateMutation.isError && (
              <div className="text-sm text-destructive">
                Failed to save: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="text-sm text-green-600">
                Post-show settings updated successfully!
              </div>
            )}
          </form>
        )}
      </div>

      {/* Public Website Section */}
      <div className="mt-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Public Website</p>
          <h2 className="text-2xl font-bold">Website Base URL</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Base URL of your public website. Used to build show page links in artist-facing emails (e.g. the archive notification sent when a show gets an archive/embed link).'
              : 'Only owners can edit website settings.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handleWebsiteSubmit} className="space-y-6 max-w-2xl mt-6">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Website base URL
              </label>
              <input
                type="url"
                value={websiteBaseUrl}
                onChange={(e) => setWebsiteBaseUrl(e.target.value)}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
                placeholder="https://radio.example.com"
              />
              <p className="text-xs text-muted-foreground">
                The show slug is appended to this URL as <code>/shows/&lt;slug&gt;</code>. Leave empty to omit the show page link from emails.
              </p>
            </div>

            {isOwner && (
              <Button type="submit" disabled={updateMutation.isPending}>
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Website Settings'}
              </Button>
            )}
          </form>
        )}
      </div>

      {/* Artist Invite Defaults Section */}
      <div className="mt-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Happenings</p>
          <h2 className="text-2xl font-bold">Artist Invite Defaults</h2>
          <p className="text-muted-foreground">
            {isOwner
              ? 'Configure the default invite settings when creating new happenings.'
              : 'Only owners can edit artist invite defaults.'}
          </p>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : (
          <form onSubmit={handleArtistInviteDefaultsSubmit} className="space-y-6 max-w-2xl mt-6">
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={defaultGenerateArtistLink}
                  onChange={(e) => setDefaultGenerateArtistLink(e.target.checked)}
                  disabled={!isOwner || updateMutation.isPending}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <div>
                  <span className="text-sm font-medium">Generate artist-facing link by default</span>
                  <p className="text-xs text-muted-foreground">
                    When checked, the "Generate artist-facing link" option will be pre-selected for new happenings.
                  </p>
                </div>
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Default Email Invitation Strategy
              </label>
              <select
                value={defaultInviteEmailStrategy}
                onChange={(e) => setDefaultInviteEmailStrategy(e.target.value as 'combined' | 'separate' | 'none')}
                disabled={!isOwner || updateMutation.isPending}
                className="w-full rounded-md border border-input bg-background px-3 py-2 disabled:opacity-50"
              >
                <option value="combined">One link, both jobs (recommended)</option>
                <option value="separate">Two emails</option>
                <option value="none">Don't email now</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Pre-selected when you create a happening, and when you open one that has never had
                an invite sent. Can always be changed per happening.
              </p>
            </div>

            {isOwner && (
              <Button
                type="submit"
                disabled={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Invite Defaults'}
              </Button>
            )}

            {updateMutation.isError && (
              <div className="text-sm text-destructive">
                Failed to save: {updateMutation.error instanceof Error ? updateMutation.error.message : 'Unknown error'}
              </div>
            )}

            {updateMutation.isSuccess && (
              <div className="text-sm text-green-600">
                Artist invite defaults updated successfully!
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
